import { css } from "styled-components";

import { styled } from "../../styled-components";
import Img from "../smart/img/img";
import { initialsFontColor, initialsBackground } from "../../lib/theme/theme";

export const EmojiDrawing = ({ str }: { str: string }) => (
    <svg viewBox="0 0 20 20">
        <text x="0.25" y="15">
            {str}
        </text>
    </svg>
);

interface ListItemStyleProps {
    interactive?: boolean;
    isCard?: boolean;
    touched?: boolean;
    allowWrapping?: boolean;
}

interface SkeletonItemProps {
    width?: string;
    height?: string;
    marginBottom?: string;
}

export const SkeletonItem = styled.div<SkeletonItemProps>`
    background-color: ${p => p.theme.skeletonColor};
    border-radius: 4px;

    width: ${p => (p.width !== undefined ? p.width : "100%")};
    height: ${p => (p.height !== undefined ? p.height : "100%")};
    margin-bottom: ${p => (p.marginBottom !== undefined ? p.marginBottom : "0")}px;
`;

export const LinkArea = styled.a`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
`;

const noWrapProps = css`
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
`;

const wrapProps = css`
    text-overflow: inherit;
    word-break: break-word;
`;

// this is done in this very weird way to optimize inflation/update time.
// this can have a dramatic impact on scrolling as we can use regular div's
// for children. We only do this here for recycled elements.
export const ListItemStyle = styled.div<ListItemStyleProps>`
    position: relative;
    padding: 0 ${p => p.theme.pageMarginMedium}px;
    transition: background-color 250ms ease-out;
    display: flex;
    align-items: center;

    min-height: ${p => p.theme.listItem.defaultRowHeight}px;

    ${p =>
        p.interactive !== true
            ? ``
            : `.builder &:hover {
        background-color: ${p.theme.listItem.selectedColor};
        cursor: pointer;
    }`};

    ${p => p.touched && `background-color: ${p.theme.listItem.selectedColor};`}

    & .topItem {
        margin-bottom: 4px;
    }

    & .textContainer {
        text-decoration: none;
        flex-grow: 1;
        flex-shrink: 1;
        width: 1px;
        padding: 12px 0;
        display: flex;
        flex-direction: column;
        justify-content: center;
    }

    & .textStyle {
        ${p => p.theme.listItem.titleFontStyle};
        color: ${p => p.theme.fgColorDark};

        ${p => (p.allowWrapping === true ? wrapProps : noWrapProps)}
    }

    & .textDetailStyle {
        ${p => p.theme.listItem.detailFontStyle};
        color: ${p => p.theme.fgColorMedium};

        ${p => (p.allowWrapping === true ? wrapProps : noWrapProps)};
    }

    & .imageBaseStyle {
        height: ${p => p.theme.listItem.mainImage.height}px;
        width: ${p => p.theme.listItem.mainImage.width}px;

        flex-shrink: 0;

        // This prevents ListItemTextContentStyle from stealing width
        min-width: ${p => p.theme.listItem.mainImage.width}px;

        background-color: ${p => p.theme.skeletonColor};
        background-repeat: no-repeat;
        background-position: center center;
        background-size: cover;
        border-radius: ${p => p.theme.listItem.mainImage.borderRadius};
        margin-right: ${p => p.theme.listItem.mainImage.marginRight}px;
    }

    & .initialsStyle {
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 24px;
        // TODO move these colors into the theme
        color: ${p => initialsFontColor(p.theme)};
        background-color: ${p => initialsBackground(p.theme)};
        font-weight: 500;
    }

    & .navigationIcon {
        color: ${p => p.theme.fgColorLight};
        width: 24px;
        height: 24px;
        min-width: 24px;

        transform: translateX(8px);

        display: flex;
        justify-content: center;
        align-items: center;
    }

    & .emojiImageStyle {
        height: ${p => p.theme.listItem.mainImage.height}px;
        width: ${p => p.theme.listItem.mainImage.width}px;

        margin-right: ${p => p.theme.listItem.mainImage.marginRight}px;

        flex-shrink: 0;
    }
`;

export const LinkCopyWrapper = styled.a`
    display: flex;
    -webkit-touch-callout: default;

    z-index: 2;

    color: inherit;
    :hover {
        color: inherit;
        text-decoration: none;
    }
`;

export const SolidIconStyle = styled.div<{ isLast: boolean }>`
    width: ${p => p.theme.listItem.actionImage.width}px;
    height: ${p => p.theme.listItem.actionImage.height}px;
    border-radius: ${p => p.theme.listItem.actionImage.borderRadius};
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: ${p => p.theme.primaryAccentColor};
    min-width: ${p => p.theme.listItem.actionImage.width}px;

    margin-left: ${p => p.theme.listItem.actionImage.margin}px;
    ${p => !p.isLast && "margin-right: " + p.theme.listItem.actionImage.margin + "px;"}

    flex-shrink: 0;
`;

const ListItemImageBase = styled(Img)`
    height: ${p => p.theme.listItem.mainImage.height}px;
    width: ${p => p.theme.listItem.mainImage.width}px;

    flex-shrink: 0;

    // This prevents ListItemTextContentStyle from stealing width
    min-width: ${p => p.theme.listItem.mainImage.width}px;

    background-color: ${p => p.theme.skeletonColor};
    object-fit: cover;
    border-radius: ${p => p.theme.listItem.mainImage.borderRadius};
    margin-right: ${p => p.theme.listItem.mainImage.marginRight}px;
`;

export const ListItemImageStyle = styled(ListItemImageBase)``;
